import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Cell,
} from 'recharts';
import { Search, ArrowRight, Calendar, Users, Hash, Trophy } from 'lucide-react';
import { toast } from '@/components/ui/Toaster';
import { apiService, queryKeys } from '@/services/api';
import type { DivisionInfo } from '@/types/api';
import { useIsMobile } from '@/hooks/useIsMobile';

const STORAGE_KEY = 'napa-recent-divisions';

export function saveRecentDivision(id: string) {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const existing: { id: string; lastAccessed: number }[] = raw ? JSON.parse(raw) : [];
    const updated = [{ id, lastAccessed: Date.now() }, ...existing.filter(d => d.id !== id)].slice(0, 5);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
  } catch { /* ignore */ }
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

type GameType = '8-ball' | 'lc';

function inferGameType(name: string | null): GameType {
  if (!name) return '8-ball';
  // Strip the quoted venue nickname to avoid false matches
  // e.g. "LEGEND'S LAGGER'S CHOICE" 8-ball League → the LC is in the venue name, not the game type
  const suffix = name.replace(/"[^"]*"/g, '').toLowerCase();
  if (/8[- ]?ball/.test(suffix)) return '8-ball';
  if (/\blc\b|lagger/.test(suffix)) return 'lc';
  return '8-ball';
}

function gameLabel(gt: GameType): string {
  return gt === '8-ball' ? '8-Ball' : "Lagger's Choice";
}

const EIGHT_BALL_COLOR = '#6366f1'; // indigo-500
const LC_COLOR = '#f59e0b';        // amber-500

function parseDate(s: string): Date {
  return new Date(s.includes('T') ? s : s + 'T12:00:00');
}

function formatDateRange(first: string | null, last: string | null): string {
  if (!first || !last) return '';
  const fmt = (d: Date) => d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  return `${fmt(parseDate(first))} – ${fmt(parseDate(last))}`;
}

function formatShortRange(first: string | null, last: string | null): string {
  if (!first || !last) return '';
  const f = parseDate(first);
  const l = parseDate(last);
  const fmtMonth = (d: Date) => d.toLocaleDateString('en-US', { month: 'short' });
  const fmtYear = (d: Date) => d.toLocaleDateString('en-US', { year: '2-digit' });
  if (f.getFullYear() === l.getFullYear()) {
    return `${fmtMonth(f)}–${fmtMonth(l)} '${fmtYear(l)}`;
  }
  return `${fmtMonth(f)} '${fmtYear(f)}–${fmtMonth(l)} '${fmtYear(l)}`;
}

// ---------------------------------------------------------------------------
// Season grouping — pair divisions by overlapping date ranges
// ---------------------------------------------------------------------------

interface Season {
  label: string;
  shortLabel: string;
  divisions: (DivisionInfo & { gameType: GameType })[];
  totalPlayers: number;
  totalMatches: number;
  firstDate: string | null;
  lastDate: string | null;
}

function groupIntoSeasons(divisions: DivisionInfo[]): Season[] {
  const tagged = divisions.map(d => ({ ...d, gameType: inferGameType(d.name) }));
  const used = new Set<string>();
  const seasons: Season[] = [];

  // Sort chronologically (oldest first) for the timeline
  const sorted = [...tagged].sort((a, b) => {
    const aDate = a.first_match_date ?? '';
    const bDate = b.first_match_date ?? '';
    return aDate.localeCompare(bDate);
  });

  for (const div of sorted) {
    if (used.has(div.division_id)) continue;
    used.add(div.division_id);

    // Find partner division with overlapping date range
    const group: typeof sorted = [div];
    for (const other of sorted) {
      if (used.has(other.division_id)) continue;
      if (!div.first_match_date || !other.first_match_date) continue;
      const daysDiff = Math.abs(
        parseDate(div.first_match_date).getTime() - parseDate(other.first_match_date).getTime()
      ) / (1000 * 60 * 60 * 24);
      if (daysDiff < 30) {
        group.push(other);
        used.add(other.division_id);
      }
    }

    // Disambiguate: if two divisions in a pair share the same inferred type,
    // one is likely misnamed. Use the full name (including quoted venue) to
    // reassign the one whose quoted name hints at the other type.
    if (group.length === 2 && group[0].gameType === group[1].gameType) {
      const fullLower0 = (group[0].name ?? '').toLowerCase();
      const fullLower1 = (group[1].name ?? '').toLowerCase();
      const has8ball0 = /8[- ]?ball/.test(fullLower0);
      const has8ball1 = /8[- ]?ball/.test(fullLower1);
      if (group[0].gameType === 'lc' && (has8ball0 !== has8ball1)) {
        // One name mentions 8-ball — that one is 8-ball, the other stays LC
        if (has8ball0) group[0].gameType = '8-ball';
        else group[1].gameType = '8-ball';
      } else if (group[0].gameType === '8-ball') {
        const hasLc0 = /\blc\b|lagger/.test(fullLower0);
        const hasLc1 = /\blc\b|lagger/.test(fullLower1);
        if (hasLc0 !== hasLc1) {
          if (hasLc0) group[0].gameType = 'lc';
          else group[1].gameType = 'lc';
        }
      }
    }

    const firstDate = group.reduce((min, d) => {
      if (!d.first_match_date) return min;
      return !min || d.first_match_date < min ? d.first_match_date : min;
    }, null as string | null);
    const lastDate = group.reduce((max, d) => {
      if (!d.last_match_date) return max;
      return !max || d.last_match_date > max ? d.last_match_date : max;
    }, null as string | null);

    seasons.push({
      label: formatDateRange(firstDate, lastDate),
      shortLabel: formatShortRange(firstDate, lastDate),
      divisions: group,
      totalPlayers: Math.max(...group.map(d => d.player_count)),
      totalMatches: group.reduce((sum, d) => sum + d.match_count, 0),
      firstDate,
      lastDate,
    });
  }

  return seasons;
}

// ---------------------------------------------------------------------------
// Timeline tooltip
// ---------------------------------------------------------------------------

function TimelineTooltip({ active, payload }: any) {
  if (!active || !payload?.length) return null;
  const data = payload[0]?.payload;
  if (!data) return null;

  return (
    <div className="bg-white rounded-lg shadow-lg border border-slate-200 p-3 text-xs min-w-[180px]">
      <div className="font-semibold text-slate-700 mb-2">{data.label}</div>
      {data.divisions?.map((div: DivisionInfo & { gameType: GameType }) => (
        <div key={div.division_id} className="flex items-center gap-2 mb-1.5">
          <div
            className="w-2 h-2 rounded-full shrink-0"
            style={{ backgroundColor: div.gameType === '8-ball' ? EIGHT_BALL_COLOR : LC_COLOR }}
          />
          <div className="flex-1">
            <div className="text-slate-600 font-medium">
              {gameLabel(div.gameType)}
              <span className="text-slate-400 font-normal ml-1">#{div.division_id}</span>
            </div>
            <div className="text-slate-400">
              {div.player_count} players · {div.match_count} matches
            </div>
          </div>
          {div.has_cached_rankings === false && (
            <span className="text-[10px] text-amber-500 font-medium">No data</span>
          )}
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Dashboard
// ---------------------------------------------------------------------------

export default function Dashboard() {
  const [divisionId, setDivisionId] = useState('');
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  const { data: divisions, isLoading: divisionsLoading } = useQuery({
    queryKey: queryKeys.divisions,
    queryFn: () => apiService.getDivisions(),
    staleTime: 5 * 60 * 1000,
  });

  const handleSearch = () => {
    const id = divisionId.trim();
    if (!id) { toast.error('Please enter a division ID'); return; }
    if (!/^\d+$/.test(id)) { toast.error('Division ID should be numeric'); return; }
    saveRecentDivision(id);
    navigate(`/season/${id}`);
  };

  const goToDivision = (id: string) => {
    saveRecentDivision(id);
    navigate(`/season/${id}`);
  };

  // Split divisions into current season (merged pair) + historical seasons
  const { currentSeason, seasons } = useMemo(() => {
    if (!divisions?.length) return { currentSeason: null, seasons: [] };

    const tagged = divisions.map(d => ({ ...d, gameType: inferGameType(d.name) }));

    // The API returns most-recent-first. "Current" = divisions whose last_match_date
    // is within 30 days of the newest division's last_match_date.
    const newest = tagged[0]?.last_match_date;
    const currentDivisions = !newest ? [] : tagged.filter(d => {
      if (!d.last_match_date) return false;
      const diff = Math.abs(
        parseDate(newest).getTime() - parseDate(d.last_match_date).getTime()
      ) / (1000 * 60 * 60 * 24);
      return diff < 30;
    });

    const currentIds = new Set(currentDivisions.map(d => d.division_id));
    const historical = divisions.filter(d => !currentIds.has(d.division_id));
    const seasons = groupIntoSeasons(historical);

    // Build a single merged current season object
    let currentSeason: {
      divisionIds: string[];
      divisions: typeof currentDivisions;
      totalPlayers: number;
      totalMatches: number;
      dateRange: string;
    } | null = null;

    if (currentDivisions.length > 0) {
      const firstDate = currentDivisions.reduce((min, d) =>
        !d.first_match_date ? min : (!min || d.first_match_date < min ? d.first_match_date : min), null as string | null);
      const lastDate = currentDivisions.reduce((max, d) =>
        !d.last_match_date ? max : (!max || d.last_match_date > max ? d.last_match_date : max), null as string | null);
      currentSeason = {
        divisionIds: currentDivisions.map(d => d.division_id),
        divisions: currentDivisions,
        totalPlayers: Math.max(...currentDivisions.map(d => d.player_count)),
        totalMatches: currentDivisions.reduce((s, d) => s + d.match_count, 0),
        dateRange: formatDateRange(firstDate, lastDate),
      };
    }

    return { currentSeason, seasons };
  }, [divisions]);

  // Bar chart data for the timeline (chronological)
  const chartData = useMemo(() => {
    return seasons.map(season => {
      const eightBall = season.divisions.find(d => d.gameType === '8-ball');
      const lc = season.divisions.find(d => d.gameType === 'lc');
      // A season "has data" if any division has cached rankings.
      // If the field is undefined (API hasn't returned it), assume data exists.
      const hasData = season.divisions.some(d => d.has_cached_rankings !== false);
      return {
        label: season.label,
        shortLabel: season.shortLabel,
        eightBallMatches: eightBall?.match_count ?? 0,
        lcMatches: lc?.match_count ?? 0,
        eightBallId: eightBall?.division_id,
        lcId: lc?.division_id,
        eightBallCached: eightBall?.has_cached_rankings,
        lcCached: lc?.has_cached_rankings,
        hasData,
        divisions: season.divisions,
        totalPlayers: season.totalPlayers,
      };
    });
  }, [seasons]);

  // Track which bar is hovered for cursor styling
  const [hoveredSeason, setHoveredSeason] = useState<number | null>(null);

  return (
    <div className="flex-1 flex flex-col">
      {/* Content */}
      <div className="max-w-5xl mx-auto px-4 w-full py-8">

        {/* Loading skeleton */}
        {divisionsLoading && (
          <div className="mb-14">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[1, 2].map(i => (
                <div key={i} className="h-28 rounded-2xl bg-slate-100 animate-pulse" />
              ))}
            </div>
          </div>
        )}

        {/* Current Season */}
        {currentSeason && (
          <div className="mb-14">
            <h2 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-4">
              Current Season
            </h2>
            <button
              onClick={() => goToDivision(currentSeason.divisionIds[0])}
              className="group w-full relative flex flex-col p-6 rounded-2xl border-2 text-left transition-all hover:shadow-lg bg-white hover:border-brand-400"
              style={{ borderColor: `${EIGHT_BALL_COLOR}40` }}
            >
              {/* Gradient accent bar */}
              <div
                className="absolute top-0 left-0 right-0 h-1 rounded-t-2xl"
                style={{ background: `linear-gradient(to right, ${EIGHT_BALL_COLOR}, ${LC_COLOR})` }}
              />

              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <Trophy className="w-5 h-5 text-brand-600" />
                  <span className="text-xl font-bold text-slate-900 group-hover:text-brand-600 transition-colors">
                    {currentSeason.divisions.length === 2 ? '8-Ball + LC' : gameLabel(currentSeason.divisions[0].gameType)}
                  </span>
                </div>
                <ArrowRight className="w-5 h-5 text-slate-300 group-hover:text-brand-500 group-hover:translate-x-0.5 transition-all" />
              </div>

              <div className="flex flex-wrap items-center gap-x-5 gap-y-1.5 text-sm text-slate-500">
                <span className="flex items-center gap-1.5 text-slate-400 font-mono text-xs">
                  {currentSeason.divisionIds.map(id => `#${id}`).join(' + ')}
                </span>
                {currentSeason.dateRange && (
                  <span className="flex items-center gap-1.5">
                    <Calendar className="w-3.5 h-3.5" />
                    {currentSeason.dateRange}
                  </span>
                )}
                <span className="flex items-center gap-1.5">
                  <Users className="w-3.5 h-3.5" />
                  {currentSeason.totalPlayers} players
                </span>
                <span className="flex items-center gap-1.5">
                  <Hash className="w-3.5 h-3.5" />
                  {currentSeason.totalMatches} matches
                </span>
              </div>
            </button>
          </div>
        )}

        {/* Season Timeline */}
        {chartData.length > 0 && (
          <div className="mb-14">
            <h2 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-1">
              League History
            </h2>
            <p className="text-xs text-slate-400 mb-4">
              Click a season to view that division
            </p>

            <div className="bg-white rounded-2xl border border-slate-200 p-5">
              {/* Legend */}
              <div className="flex items-center gap-4 mb-4 text-xs text-slate-500">
                <span className="flex items-center gap-1.5">
                  <span className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: EIGHT_BALL_COLOR }} />
                  8-Ball matches
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: LC_COLOR }} />
                  LC matches
                </span>
              </div>

              <ResponsiveContainer width="100%" height={220}>
                <BarChart
                  data={chartData}
                  margin={{ top: 5, right: 5, left: -10, bottom: 5 }}
                  onMouseMove={(state: any) => {
                    setHoveredSeason(state?.activeTooltipIndex ?? null);
                  }}
                  onMouseLeave={() => setHoveredSeason(null)}
                  onClick={(state: any) => {
                    if (state?.activeTooltipIndex == null) return;
                    const item = chartData[state.activeTooltipIndex];
                    if (!item) return;
                    const id = item.eightBallId || item.lcId;
                    if (id) goToDivision(id);
                  }}
                  style={{ cursor: hoveredSeason != null ? 'pointer' : 'default' }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                  <XAxis
                    dataKey="shortLabel"
                    fontSize={10}
                    stroke="#94a3b8"
                    tickLine={false}
                    axisLine={false}
                    interval={0}
                    angle={isMobile ? 0 : -30}
                    textAnchor={isMobile ? 'middle' : 'end'}
                    height={isMobile ? 30 : 50}
                  />
                  <YAxis
                    fontSize={10}
                    stroke="#94a3b8"
                    tickLine={false}
                    axisLine={false}
                    width={35}
                  />
                  <Tooltip content={<TimelineTooltip />} cursor={{ fill: '#f8fafc' }} />
                  <Bar dataKey="eightBallMatches" stackId="a" radius={[0, 0, 0, 0]}>
                    {chartData.map((entry, idx) => (
                      <Cell
                        key={idx}
                        fill={!entry.hasData
                          ? `${EIGHT_BALL_COLOR}30`
                          : hoveredSeason === idx ? EIGHT_BALL_COLOR : `${EIGHT_BALL_COLOR}cc`
                        }
                      />
                    ))}
                  </Bar>
                  <Bar dataKey="lcMatches" stackId="a" radius={[4, 4, 0, 0]}>
                    {chartData.map((entry, idx) => (
                      <Cell
                        key={idx}
                        fill={!entry.hasData
                          ? `${LC_COLOR}30`
                          : hoveredSeason === idx ? LC_COLOR : `${LC_COLOR}cc`
                        }
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
        {/* Division Search */}
        <div className="max-w-sm mx-auto">
          <div className="flex rounded-lg overflow-hidden border border-slate-200 shadow-sm">
            <input
              type="text"
              className="flex-1 px-4 py-2.5 bg-white text-slate-800 placeholder-slate-400 border-0 focus:outline-none text-sm"
              placeholder="Enter division ID (e.g. 13476)"
              value={divisionId}
              onChange={(e) => setDivisionId(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            />
            <button
              onClick={handleSearch}
              disabled={!divisionId.trim()}
              className="px-4 bg-brand-500 hover:bg-brand-400 disabled:opacity-40 text-white text-sm font-medium transition-colors flex items-center gap-1.5"
            >
              <Search className="w-3.5 h-3.5" />
              Go
            </button>
          </div>
          <p className="text-[11px] text-slate-400 mt-1.5 text-center">
            Find your division ID in the league standings URL on poolshooters.com
          </p>
        </div>
      </div>
    </div>
  );
}
